import { useState, useEffect, ChangeEvent } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from '@mui/material';
import CenteredCircularProgress from 'components/CenteredCircularProgress';
import { useStorageMetrics } from './api/getStorageMetrics';
const bytes = require('bytes');

interface Props {
  open: boolean;
  handleClose: () => void;
}

const MetricCalculator = ({ open, handleClose }: Props) => {
  const [percentageToRetain, setPercentageToRetain] = useState<string>('30');
  const [recommendedPurgeInterval, setRecommendedPurgeInterval] =
    useState<number>(0);
  const { data: metrics, isLoading } = useStorageMetrics({
    config: { refetchInterval: 30000 },
  });

  const calculateRecommendedPurgeInterval = (): number => {
    if (!metrics || percentageToRetain.length === 0) return 0;
    const diskSpaceInBytes = bytes.parse(metrics.disk_size);
    const formattedDataRate = metrics.data_rate.slice(
      0,
      metrics.data_rate.length - 2
    );
    const dataRateInBytes = bytes.parse(formattedDataRate);
    const adjustedDiskSpace =
      diskSpaceInBytes * ((100 - Number(percentageToRetain)) / 100.0);
    const bytesPerDay = dataRateInBytes * 86400;
    const daysUntilFull = adjustedDiskSpace / bytesPerDay;

    return daysUntilFull < 1 ? 1 : Math.ceil(daysUntilFull);
  };

  useEffect(() => {
    const purgeIntervalInDays = calculateRecommendedPurgeInterval();
    setRecommendedPurgeInterval(purgeIntervalInDays);
    //eslint-disable-next-line
  }, [metrics, percentageToRetain]);

  if (!metrics) return <></>;

  if (isLoading) return <CenteredCircularProgress />;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (event.target.value.length > 0 && value <= 0) {
      setPercentageToRetain('1');
      return;
    }

    if (value > 100) {
      setPercentageToRetain('100');
      return;
    }

    setPercentageToRetain(event.target.value);
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Storage Calculator</DialogTitle>
      <DialogContent>
        <TextField
          margin='dense'
          id='data_rate'
          label='Data Rate'
          fullWidth
          value={metrics.data_rate}
          disabled
        />
        <TextField
          margin='dense'
          id='total_database_size'
          label='Database Size'
          fullWidth
          value={metrics.database_size}
          disabled
        />
        <TextField
          margin='dense'
          id='total_disk_free_space'
          label='Total Disk Space'
          fullWidth
          value={metrics.disk_size}
          disabled
        />
        <TextField
          type='number'
          margin='dense'
          id='percentage_to_retain'
          label='Disk % to retain'
          fullWidth
          InputProps={{
            endAdornment: <InputAdornment position='end'>%</InputAdornment>,
          }}
          value={percentageToRetain}
          onChange={handleChange}
        />
        <TextField
          margin='dense'
          id='recommend_purge_interval'
          label='Recommended Purge Interval (Estimate)'
          fullWidth
          InputProps={{
            endAdornment: <InputAdornment position='end'>days</InputAdornment>,
          }}
          value={recommendedPurgeInterval}
          disabled
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetricCalculator;
